import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DeviceManagement = () => {
  const [devices, setDevices] = useState([
    {
      id: 1,
      name: 'MacBook Pro 16"',
      type: 'desktop',
      icon: 'Monitor',
      location: 'San Francisco, CA',
      ipAddress: '192.168.1.105',
      browser: 'Chrome 120.0',
      lastActive: 'Active now',
      isCurrent: true,
      trusted: true,
      firstSeen: 'Dec 15, 2025'
    },
    {
      id: 2,
      name: 'iPhone 15 Pro',
      type: 'mobile',
      icon: 'Smartphone',
      location: 'San Francisco, CA',
      ipAddress: '192.168.1.108',
      browser: 'Safari Mobile',
      lastActive: '2 hours ago',
      isCurrent: false,
      trusted: true,
      firstSeen: 'Dec 20, 2025'
    },
    {
      id: 3,
      name: 'iPad Air',
      type: 'tablet',
      icon: 'Tablet',
      location: 'Oakland, CA',
      ipAddress: '192.168.1.112',
      browser: 'Safari 17.2',
      lastActive: '1 day ago',
      isCurrent: false,
      trusted: true,
      firstSeen: 'Jan 5, 2026'
    },
    {
      id: 4,
      name: 'Windows Desktop',
      type: 'desktop',
      icon: 'Monitor',
      location: 'New York, NY',
      ipAddress: '203.45.67.89',
      browser: 'Edge 120.0',
      lastActive: '3 days ago',
      isCurrent: false,
      trusted: false,
      firstSeen: 'Jan 10, 2026'
    }
  ]);

  const handleRemoveDevice = (deviceId) => {
    setDevices(devices?.filter(d => d?.id !== deviceId));
  };

  const handleTrustDevice = (deviceId) => {
    setDevices(devices?.map(d => 
      d?.id === deviceId ? { ...d, trusted: !d?.trusted } : d
    ));
  };

  return (
    <div className="bg-card rounded-lg shadow-md p-4 md:p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-secondary/10 rounded-lg flex items-center justify-center">
            <Icon name="Laptop" size={24} color="var(--color-secondary)" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-semibold text-foreground">Device Management</h2>
            <p className="text-sm text-muted-foreground">Monitor and control access across all your devices</p>
          </div>
        </div>
        <div className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
          {devices?.length} Active
        </div>
      </div>
      <div className="bg-accent/10 border border-accent/30 rounded-lg p-4 mb-6 flex items-start gap-3">
        <Icon name="Info" size={20} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-foreground">
            <strong>Security Tip:</strong> Regularly review your active devices and remove any you don't recognize. Enable trusted device status for your primary devices to streamline future logins.
          </p>
        </div>
      </div>
      <div className="space-y-4">
        {devices?.map((device) => (
          <div key={device?.id} className={`border rounded-lg p-4 md:p-5 transition-all ${device?.isCurrent ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}>
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${device?.trusted ? 'bg-success/10' : 'bg-warning/10'}`}>
                  <Icon name={device?.icon} size={24} color={device?.trusted ? 'var(--color-success)' : 'var(--color-warning)'} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="text-base md:text-lg font-medium text-foreground">{device?.name}</h3>
                    {device?.isCurrent && (
                      <span className="px-2 py-0.5 bg-primary text-primary-foreground text-xs font-medium rounded">Current Device</span>
                    )}
                    {device?.trusted && (
                      <span className="px-2 py-0.5 bg-success/10 text-success text-xs font-medium rounded flex items-center gap-1">
                        <Icon name="ShieldCheck" size={12} />
                        Trusted
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Icon name="MapPin" size={14} />
                      <span className="truncate">{device?.location}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="Globe" size={14} />
                      <span className="truncate">{device?.browser}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="Network" size={14} />
                      <span className="truncate">{device?.ipAddress}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="Clock" size={14} />
                      <span className="truncate">{device?.lastActive}</span>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">
                    First seen: {device?.firstSeen}
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 lg:flex-col lg:w-32">
                {!device?.isCurrent && (
                  <>
                    <Button
                      variant={device?.trusted ? 'outline' : 'default'}
                      size="sm"
                      onClick={() => handleTrustDevice(device?.id)}
                      fullWidth
                    >
                      {device?.trusted ? 'Untrust' : 'Trust'}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleRemoveDevice(device?.id)}
                      fullWidth
                    >
                      Remove
                    </Button>
                  </>
                )}
                {device?.isCurrent && (
                  <Button variant="outline" size="sm" disabled fullWidth>
                    Current
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-base font-medium text-foreground mb-1">Device Security Settings</h3>
            <p className="text-sm text-muted-foreground">Configure how new devices are handled</p>
          </div>
          <Button variant="outline" iconName="Settings" iconPosition="left" size="sm">
            Configure Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DeviceManagement;